"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Loader, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const projectTypes = [{
  value: "Essential",
  label: "Essential Wash"
}, {
  value: "Interior",
  label: "Interior Deep"
}, {
  value: "Full",
  label: "Full Detailing"
}, {
  value: "Paint",
  label: "Paint Correction"
}, {
  value: "Ceramic",
  label: "Ceramic Coating"
}];
const contactRows = [{
  label: "WhatsApp",
  value: "+1 786-778-4394"
}, {
  label: "Email",
  value: "valeroelitedetailing@gmail.com"
}, {
  label: "Service",
  value: "Mobile service across Miami-Dade"
}];
function TalkForm() {
  const prefersReducedMotion = useReducedMotion();
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-[hsl(258_14%_14%)] bg-[hsl(258_16%_9%)] p-10" style={{
      borderRadius: "var(--radius)"
    }}>
        <div className="mb-4 text-[11px] font-medium uppercase" style={{
        color: "hsl(41 62% 62%)",
        letterSpacing: "0.22em"
      }}>
          Sent
        </div>
        <p className="font-serif text-3xl font-light leading-tight text-[hsl(38_32%_92%)]">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 h-auto border-[hsl(258_14%_14%)] bg-transparent px-6 py-3 text-xs uppercase text-[hsl(38_32%_92%)] hover:border-[hsl(41_62%_62%)] hover:bg-transparent hover:text-[hsl(41_62%_62%)]" style={{
        letterSpacing: "0.14em",
        borderRadius: "9999px"
      }}>
          New request
        </Button>
      </div>;
  }
  const fieldMotion = prefersReducedMotion ? {} : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.7,
      ease: [0.2, 0.7, 0.1, 1] as [number, number, number, number]
    }
  };
  return <Form onSubmit={handleSubmit} className="space-y-10">
      {[{
      key: "name",
      index: 0
    }, {
      key: "email",
      index: 1
    }, {
      key: "project_type",
      index: 2
    }, {
      key: "message",
      index: 3
    }].map((f, i) => <motion.div key={f.key} data-index={i} {...fieldMotion} transition={prefersReducedMotion ? undefined : {
      duration: 0.7,
      delay: i * 0.1,
      ease: [0.2, 0.7, 0.1, 1] as [number, number, number, number]
    }}>
          {f.key === "name" && <FormField control={control} name="name" rules={{
        required: "Name required"
      }} render={({
        field
      }) => <FormItem className="gap-3">
                  <FormLabel className="text-[11px] font-medium uppercase text-[hsl(38_14%_68%)]" style={{
          letterSpacing: "0.18em"
        }}>
                    Name
                    <span className="ml-1.5 inline-block h-1 w-1 rounded-lg align-middle" style={{
            backgroundColor: "hsl(41 62% 62%)"
          }} />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Your name" className="h-auto border-0 border-b border-[hsl(258_14%_14%)] bg-transparent px-0 py-3 text-base text-[hsl(38_32%_92%)] transition-colors placeholder:text-[hsl(38_14%_68%)]/60 focus-visible:border-[hsl(41_62%_62%)] focus-visible:ring-0" />
                  </FormControl>
                  <FormMessage className="flex items-center gap-1.5 text-xs" />
                </FormItem>} />}

          {f.key === "email" && <FormField control={control} name="email" rules={{
        required: "Email required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
                  <FormLabel className="text-[11px] font-medium uppercase text-[hsl(38_14%_68%)]" style={{
          letterSpacing: "0.18em"
        }}>
                    Email
                    <span className="ml-1.5 inline-block h-1 w-1 rounded-lg align-middle" style={{
            backgroundColor: "hsl(41 62% 62%)"
          }} />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="email@ejemplo.com" className="h-auto border-0 border-b border-[hsl(258_14%_14%)] bg-transparent px-0 py-3 text-base text-[hsl(38_32%_92%)] transition-colors placeholder:text-[hsl(38_14%_68%)]/60 focus-visible:border-[hsl(41_62%_62%)] focus-visible:ring-0" />
                  </FormControl>
                  <FormMessage className="flex items-center gap-1.5 text-xs" />
                </FormItem>} />}

          {f.key === "project_type" && <FormField control={control} name="project_type" rules={{
        required: "Select service"
      }} render={({
        field
      }) => <FormItem className="gap-3">
                  <FormLabel className="text-[11px] font-medium uppercase text-[hsl(38_14%_68%)]" style={{
          letterSpacing: "0.18em"
        }}>
                    Service
                    <span className="ml-1.5 inline-block h-1 w-1 rounded-lg align-middle" style={{
            backgroundColor: "hsl(41 62% 62%)"
          }} />
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-auto w-full border-0 border-b border-[hsl(258_14%_14%)] bg-transparent px-0 py-3 text-base text-[hsl(38_32%_92%)] focus:ring-0 focus-visible:border-[hsl(41_62%_62%)] focus-visible:ring-0 data-[placeholder]:text-[hsl(38_14%_68%)]/60">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="border-[hsl(258_14%_14%)] bg-[hsl(258_16%_9%)] text-[hsl(38_32%_92%)]">
                      {projectTypes.map((t, idx) => <SelectItem data-index={idx} key={t.value} value={t.value} className="text-[hsl(38_32%_92%)] focus:bg-[hsl(41_62%_62%)]/15 focus:text-[hsl(38_32%_92%)]">
                          {t.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage className="flex items-center gap-1.5 text-xs" />
                </FormItem>} />}

          {f.key === "message" && <FormField control={control} name="message" rules={{
        required: "Message required",
        minLength: {
          value: 10,
          message: "Minimum 10 characters"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
                  <FormLabel className="text-[11px] font-medium uppercase text-[hsl(38_14%_68%)]" style={{
          letterSpacing: "0.18em"
        }}>
                    Details
                    <span className="ml-1.5 inline-block h-1 w-1 rounded-lg align-middle" style={{
            backgroundColor: "hsl(41 62% 62%)"
          }} />
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Modelo de auto y ubicación" className="min-h-[140px] border-0 border-b border-[hsl(258_14%_14%)] bg-transparent px-0 py-3 text-base text-[hsl(38_32%_92%)] transition-colors placeholder:text-[hsl(38_14%_68%)]/60 focus-visible:border-[hsl(41_62%_62%)] focus-visible:ring-0" />
                  </FormControl>
                  <FormMessage className="flex items-center gap-1.5 text-xs" />
                </FormItem>} />}
        </motion.div>)}

      <motion.div {...fieldMotion} transition={prefersReducedMotion ? undefined : {
      duration: 0.7,
      delay: 0.45,
      ease: [0.2, 0.7, 0.1, 1] as [number, number, number, number]
    }}>
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-[11px] font-medium uppercase text-[hsl(38_14%_68%)]" style={{
          letterSpacing: "0.18em"
        }}>
                Phone
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="(305) 000-0000" className="h-auto border-0 border-b border-[hsl(258_14%_14%)] bg-transparent px-0 py-3 text-base text-[hsl(38_32%_92%)] transition-colors placeholder:text-[hsl(38_14%_68%)]/60 focus-visible:border-[hsl(41_62%_62%)] focus-visible:ring-0" />
              </FormControl>
              <FormMessage className="flex items-center gap-1.5 text-xs" />
            </FormItem>} />
      </motion.div>

      {submitError && <div className="flex items-start gap-3 border border-[hsl(4_62%_62%)]/40 bg-[hsl(4_62%_62%)]/10 p-4">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-[hsl(4_62%_62%)]" />
          <p className="text-sm text-[hsl(4_62%_62%)]">{submitError}</p>
        </div>}

      <motion.div {...fieldMotion} transition={prefersReducedMotion ? undefined : {
      duration: 0.7,
      delay: 0.6,
      ease: [0.2, 0.7, 0.1, 1] as [number, number, number, number]
    }} className="flex flex-col items-stretch gap-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-[hsl(38_14%_68%)]">
          Privacidad garantizada.
        </p>

        <div className="relative">
          {!prefersReducedMotion && <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" animate={{
          scale: [1, 1.03, 1],
          opacity: [0.18, 0.24, 0.18]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }} style={{
          background: "radial-gradient(closest-side, hsla(41,62%,62%,0.35), transparent 70%)",
          filter: "blur(40px)",
          transform: "scale(1.6)"
        }} />}
          <Button type="submit" disabled={isSubmitting} className="h-auto border border-[hsl(38_32%_92%)]/10 bg-[hsl(38_32%_92%)]/[0.06] px-7 py-3.5 text-xs font-medium uppercase text-[hsl(38_32%_92%)] backdrop-blur-md transition-all hover:border-[hsl(41_62%_62%)] hover:bg-[hsl(41_62%_62%)]/15 hover:text-[hsl(38_32%_92%)] w-full sm:w-auto" style={{
          letterSpacing: "0.14em",
          borderRadius: "9999px"
        }}>
            {isSubmitting ? <>
                <Loader className="mr-2 size-4 animate-spin" />
                Sending
              </> : <>
                Reservar
                <ArrowRight className="ml-1 size-4" />
              </>}
          </Button>
        </div>
      </motion.div>
    </Form>;
}
export default function Appointment() {
  const prefersReducedMotion = useReducedMotion();
  const headlineWords = [{
    text: "Reserve",
    italic: false
  }, {
    text: "su",
    italic: false
  }, {
    text: "cita",
    italic: true
  }, {
    text: "de",
    italic: false
  }, {
    text: "detailing",
    italic: false
  }, {
    text: "premium",
    italic: false
  }, {
    text: "hoy.",
    italic: false
  }];
  return <section data-section-id="28694" id="secure-your-appointment-asegure-su-cita" className="relative overflow-hidden bg-background text-foreground py-24 md:py-36 lg:py-44">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-0 opacity-40" style={{
      backgroundImage: "radial-gradient(ellipse 60% 40% at 20% 90%, hsla(41,62%,62%,0.12), transparent 70%)"
    }} />

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 md:px-10 lg:grid-cols-12 lg:gap-12 lg:px-[72px]">
        <div className="lg:col-span-5 lg:col-start-2">
          <motion.div initial={prefersReducedMotion ? undefined : {
          opacity: 0,
          y: 20
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.8,
          ease: [0.2, 0.7, 0.1, 1]
        }} className="text-[11px] font-medium uppercase text-[hsl(38_14%_68%)]" style={{
          letterSpacing: "0.22em"
        }}>
            Contact
          </motion.div>

          <h1 className="mt-8 font-serif font-light leading-[1.02] text-[hsl(38_32%_92%)]" style={{
          fontSize: "clamp(2.5rem, 5.6vw, 5.5rem)",
          letterSpacing: "-0.02em"
        }}>Get in Touch</h1>

          

          <div className="mt-12 space-y-6">
            {contactRows.map((row, i) => <motion.div key={row.label} data-index={i} initial={prefersReducedMotion ? undefined : {
            opacity: 0,
            y: 16
          }} whileInView={prefersReducedMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={prefersReducedMotion ? undefined : {
            duration: 0.7,
            delay: 0.7 + i * 0.1,
            ease: [0.2, 0.7, 0.1, 1] as [number, number, number, number]
          }}>
                <div className="text-[11px] font-medium uppercase text-[hsl(38_14%_68%)]" style={{
              letterSpacing: "0.22em"
            }}>
                  {row.label}
                </div>
                <div className="mt-2 text-base text-[hsl(38_32%_92%)]">
                  {row.value}
                </div>
                {i < contactRows.length - 1 && <div className="mt-6 h-px w-10" style={{
              backgroundColor: "hsl(258 14% 14%)"
            }} />}
              </motion.div>)}
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <motion.div initial={prefersReducedMotion ? undefined : {
          opacity: 0,
          y: 24
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.9,
          ease: [0.2, 0.7, 0.1, 1]
        }}>
            <FormProvider formKey="appointment_form_contact" formId="appointment_form_contact" sectionName="secure-your-appointment-asegure-su-cita" defaultValues={{
            name: "",
            email: "",
            phone: "",
            project_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            project_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Reservar" successMessage="Solicitud recibida. Confirmaremos pronto." errorMessage="Error al enviar. Intente de nuevo.">
              <TalkForm />
            </FormProvider>
          </motion.div>
        </div>
      </div>
    </section>;
}